import { useCallback, useEffect } from "react";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { CloseTemplateTasksDrawerContent } from "./CloseTemplateTasksDrawerContent";
import { ApplyCloseTemplateDrawer } from "./ApplyCloseTemplateDrawer";
import { useCloseTemplateTasks } from "./useCloseTemplateTasks";

interface CreateTemplateTasksStepProps {
    closeTemplateId: string;
    onClose: () => void;
    /** Registers the close-attempt guard so the outer Drawer can delegate to it. */
    onRegisterCloseAttempt: (fn: (cancel: () => void) => void) => void;
    /** Notifies parent when save state changes so the Drawer can set dismissible. */
    onSavingChange: (isSaving: boolean) => void;
}

export const CreateTemplateTasksStep = ({
    closeTemplateId,
    onClose,
    onRegisterCloseAttempt,
    onSavingChange,
}: CreateTemplateTasksStepProps) => {
    const { toast: infoToast } = useToaster();

    const {
        localTasks,
        stages,
        currentTemplate,
        isReady,
        isDirty,
        isSaving,
        isConfirmDiscardOpen,
        isApplyDrawerOpen,
        handleLocalTasksChange,
        handleSave,
        handleSaveAndApply,
        handleOpenApplyDrawer,
        handleCloseAttempt,
        handleSaveAndExit,
        handleKeepEditing,
    } = useCloseTemplateTasks({
        closeTemplateId,
        isFetchEnabled: true,
        onClose,
    });

    useEffect(() => {
        onSavingChange(isSaving);
    }, [isSaving, onSavingChange]);

    useEffect(() => {
        onRegisterCloseAttempt(handleCloseAttempt);
    }, [handleCloseAttempt, onRegisterCloseAttempt]);

    const handleConfirmDiscardWithToast = useCallback(() => {
        infoToast("Template created. You can add tasks later from the list.");
        onClose();
    }, [onClose, infoToast]);

    if (!isReady || !stages) {
        return <Loader />;
    }

    return (
        <>
            <CloseTemplateTasksDrawerContent
                key={closeTemplateId}
                localTasks={localTasks}
                onLocalTasksChange={handleLocalTasksChange}
                stages={stages}
                isSaving={isSaving}
                isDirty={isDirty}
                onSave={handleSave}
                onSaveAndApply={handleSaveAndApply}
                onApply={handleOpenApplyDrawer}
                basis={currentTemplate?.basis ?? null}
                workspaceId={currentTemplate?.workspaceId ?? null}
            />

            {isApplyDrawerOpen && (
                <ApplyCloseTemplateDrawer
                    key={currentTemplate?.id ?? "none"}
                    open={isApplyDrawerOpen}
                    closeTemplate={currentTemplate}
                    onClose={() => onClose()}
                />
            )}

            <ConfirmationModal
                show={isConfirmDiscardOpen}
                title="Unsaved changes"
                question="You have unsaved changes. What would you like to do?"
                yes="Discard changes"
                yesBtnVariant="destructive"
                no="Save changes & exit"
                noBtnVariant="secondary"
                onConfirm={handleConfirmDiscardWithToast}
                onReject={handleSaveAndExit}
                onHide={handleKeepEditing}
            />
        </>
    );
};
